const SOUND_PATH = '/audio/chat-ding.mp3';
const DEFAULT_ICON = '/favicon.ico';

const isNotificationSupported = (): boolean => typeof window !== 'undefined' && 'Notification' in window;

export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

export const requestNotificationPermission = async (): Promise<NotificationPermission | 'unsupported'> => {
  if (!isNotificationSupported()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
};

interface ShowNotificationOptions {
  title: string;
  body?: string;
  tag?: string;
  icon?: string;
  onClick?: () => void;
}

/**
 * OS 알림 표시. 권한이 없거나 미지원이면 null 반환(silent fail).
 * tag 동일 시 OS가 동일 채널 알림을 합쳐 표시한다.
 */
export const showSystemNotification = ({
  title,
  body,
  tag,
  icon = DEFAULT_ICON,
  onClick,
}: ShowNotificationOptions): Notification | null => {
  if (!isNotificationSupported() || Notification.permission !== 'granted') return null;
  try {
    const notif = new Notification(title, { body, tag, icon });
    if (onClick) {
      notif.onclick = (event) => {
        event.preventDefault();
        try {
          window.focus();
        } catch {
          // ignore
        }
        onClick();
        notif.close();
      };
    }
    return notif;
  } catch {
    return null;
  }
};

let audioInstance: HTMLAudioElement | null = null;
let audioUnlocked = false;

const getAudio = (): HTMLAudioElement | null => {
  if (typeof window === 'undefined') return null;
  if (!audioInstance) {
    audioInstance = new Audio(SOUND_PATH);
    audioInstance.preload = 'auto';
  }
  return audioInstance;
};

/**
 * 첫 user gesture 시점에 호출하여 자동재생 정책 우회.
 * 무음 play 후 즉시 정지.
 */
export const unlockNotificationSound = (): void => {
  const audio = getAudio();
  if (!audio || audioUnlocked) return;
  const prevVolume = audio.volume;
  audio.volume = 0;
  audio
    .play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = prevVolume;
      audioUnlocked = true;
    })
    .catch(() => {
      audio.volume = prevVolume;
    });
};

export const playNotificationSound = (): void => {
  const audio = getAudio();
  if (!audio) return;
  audio.currentTime = 0;
  audio.play().catch(() => {
    // 사용자 제스처 미발생 등으로 거부될 수 있음 - 무시
  });
};
